// WebRTC PTT Engine — full-mesh topology, max 6 peers
import type { SignalMessage } from '../types/radio'

export type RTCStatus = 'idle' | 'connecting' | 'connected' | 'failed' | 'closed'

export interface PeerState { userId: string; status: RTCStatus; speaking: boolean }

export interface PTTEngineCallbacks {
  onPeerUpdate: (peers: PeerState[]) => void
  onSpeaking: (userId: string, speaking: boolean) => void
  onTransmitting: (transmitting: boolean) => void
  onError: (msg: string) => void
  sendSignal: (msg: Omit<SignalMessage, 'from' | 'channelId'>) => void
}

const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true, noiseSuppression: true, autoGainControl: true,
  channelCount: 1, sampleRate: 24000,
}

export class PTTEngine {
  private readonly userId: string
  private readonly cb: PTTEngineCallbacks
  private localStream: MediaStream | null = null
  private audioCtx: AudioContext | null = null
  private analyser: AnalyserNode | null = null
  private vuSource: MediaStreamAudioSourceNode | null = null
  private vuRafId: number | null = null
  private peers = new Map<string, RTCPeerConnection>()
  private peerState = new Map<string, PeerState>()
  private remoteAudio = new Map<string, HTMLAudioElement>()
  private transmitting = false
  private destroyed = false

  constructor(userId: string, _channelId: string, callbacks: PTTEngineCallbacks) {
    this.userId = userId
    this.cb = callbacks
  }

  async init(): Promise<void> {
    if (this.localStream) return
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS, video: false })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Microphone access denied'
      this.cb.onError(`Микрофон: ${msg}`)
      throw err
    }
    this.localStream.getAudioTracks().forEach((t) => { t.enabled = false })
    this.setupVU()
  }

  async connectToPeer(peerId: string): Promise<void> {
    if (this.peers.has(peerId) || peerId === this.userId) return
    const pc = this.createPeerConnection(peerId)
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    this.cb.sendSignal({ type: 'offer', to: peerId, payload: { sdp: pc.localDescription } })
  }

  async handleSignal(msg: SignalMessage): Promise<void> {
    if (msg.to !== this.userId) return
    if (this.destroyed) return
    const { type, from, payload } = msg
    switch (type) {
      case 'offer': await this.handleOffer(from, payload); break
      case 'answer': await this.handleAnswer(from, payload); break
      case 'ice-candidate': await this.handleICE(from, payload); break
      case 'bye': this.removePeer(from); break
    }
  }

  startTransmit(): void {
    if (!this.localStream) return
    this.localStream.getAudioTracks().forEach((t) => { t.enabled = true })
    this.transmitting = true
    this.cb.onTransmitting(true)
    this.startVUMeter()
  }

  stopTransmit(): void {
    if (!this.localStream) return
    this.localStream.getAudioTracks().forEach((t) => { t.enabled = false })
    this.transmitting = false
    this.cb.onTransmitting(false)
    this.stopVUMeter()
  }

  destroy(): void {
    this.destroyed = true
    this.stopTransmit()
    this.stopVUMeter()
    for (const peerId of this.peers.keys()) {
      this.cb.sendSignal({ type: 'bye', to: peerId, payload: null })
    }
    this.peers.forEach((pc) => pc.close())
    this.peers.clear()
    this.peerState.clear()
    this.remoteAudio.forEach((el) => { el.srcObject = null; el.remove() })
    this.remoteAudio.clear()
    this.localStream?.getTracks().forEach((t) => t.stop())
    this.localStream = null
    this.vuSource?.disconnect()
    this.analyser?.disconnect()
    this.audioCtx?.close().catch(() => {})
    this.audioCtx = null
    this.analyser = null
    this.vuSource = null
  }

  getAnalyser(): AnalyserNode | null { return this.analyser }
  getPeers(): PeerState[] { return [...this.peerState.values()] }
  isTransmitting(): boolean { return this.transmitting }

  private createPeerConnection(peerId: string): RTCPeerConnection {
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: 'stun:stun.l.google.com:19302' }, { urls: 'stun:stun1.l.google.com:19302' }],
    })
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => { pc.addTrack(track, this.localStream!) })
    }
    pc.onicecandidate = ({ candidate }) => {
      if (candidate) this.cb.sendSignal({ type: 'ice-candidate', to: peerId, payload: { candidate } })
    }
    pc.onconnectionstatechange = () => {
      const status = this.mapConnState(pc.connectionState)
      this.updatePeer(peerId, { status })
      if (pc.connectionState === 'failed') pc.restartIce()
    }
    pc.ontrack = ({ streams }) => {
      const stream = streams[0]
      if (!stream) return
      this.monitorRemoteLevel(peerId, stream)
      let audio = this.remoteAudio.get(peerId)
      if (!audio) {
        audio = document.createElement('audio')
        audio.autoplay = true
        ;(audio as HTMLAudioElement & { playsInline: boolean }).playsInline = true
        document.body.appendChild(audio)
        this.remoteAudio.set(peerId, audio)
      }
      audio.srcObject = stream
      void audio.play().catch(() => {})
    }
    this.peers.set(peerId, pc)
    this.updatePeer(peerId, { status: 'connecting', speaking: false })
    return pc
  }

  private async handleOffer(from: string, payload: unknown): Promise<void> {
    const { sdp } = payload as { sdp: RTCSessionDescriptionInit }
    const pc = this.peers.get(from) ?? this.createPeerConnection(from)
    await pc.setRemoteDescription(new RTCSessionDescription(sdp))
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    this.cb.sendSignal({ type: 'answer', to: from, payload: { sdp: pc.localDescription } })
  }

  private async handleAnswer(from: string, payload: unknown): Promise<void> {
    const pc = this.peers.get(from)
    if (!pc) return
    const { sdp } = payload as { sdp: RTCSessionDescriptionInit }
    await pc.setRemoteDescription(new RTCSessionDescription(sdp))
  }

  private async handleICE(from: string, payload: unknown): Promise<void> {
    const pc = this.peers.get(from)
    if (!pc) return
    const { candidate } = payload as { candidate: RTCIceCandidateInit }
    await pc.addIceCandidate(new RTCIceCandidate(candidate))
  }

  private removePeer(peerId: string): void {
    this.peers.get(peerId)?.close()
    this.peers.delete(peerId)
    this.peerState.delete(peerId)
    const audio = this.remoteAudio.get(peerId)
    if (audio) { audio.srcObject = null; audio.remove() }
    this.remoteAudio.delete(peerId)
    this.notifyPeerUpdate()
  }

  private updatePeer(peerId: string, patch: Partial<PeerState>): void {
    const current = this.peerState.get(peerId) ?? { userId: peerId, status: 'idle' as RTCStatus, speaking: false }
    this.peerState.set(peerId, { ...current, ...patch })
    this.notifyPeerUpdate()
  }

  private notifyPeerUpdate(): void { this.cb.onPeerUpdate([...this.peerState.values()]) }

  private mapConnState(state: RTCPeerConnectionState): RTCStatus {
    switch (state) {
      case 'connecting': case 'new': return 'connecting'
      case 'connected': return 'connected'
      case 'failed': return 'failed'
      case 'closed': case 'disconnected': return 'closed'
      default: return 'idle'
    }
  }

  private setupVU(): void {
    try {
      this.audioCtx = new AudioContext({ sampleRate: 24000 })
      this.analyser = this.audioCtx.createAnalyser()
      this.analyser.fftSize = 256
      this.analyser.smoothingTimeConstant = 0.8
      if (this.localStream) {
        this.vuSource = this.audioCtx.createMediaStreamSource(this.localStream)
        this.vuSource.connect(this.analyser)
      }
    } catch { /* AudioContext not available */ }
  }

  private startVUMeter(): void {
    if (!this.analyser) return
    const buf = new Uint8Array(new ArrayBuffer(this.analyser.frequencyBinCount))
    const tick = () => {
      if (!this.transmitting) return
      this.analyser!.getByteFrequencyData(buf)
      this.vuRafId = requestAnimationFrame(tick)
    }
    this.vuRafId = requestAnimationFrame(tick)
  }

  private stopVUMeter(): void {
    if (this.vuRafId !== null) { cancelAnimationFrame(this.vuRafId); this.vuRafId = null }
  }

  private monitorRemoteLevel(peerId: string, stream: MediaStream): void {
    if (!this.audioCtx) return
    const analyser = this.audioCtx.createAnalyser()
    analyser.fftSize = 256
    const source = this.audioCtx.createMediaStreamSource(stream)
    source.connect(analyser)
    const buf = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount))
    let speaking = false
    const tick = () => {
      if (this.destroyed || !this.peers.has(peerId)) return
      analyser.getByteFrequencyData(buf)
      const avg = buf.reduce((a, b) => a + b, 0) / buf.length
      const nowSpeaking = avg > 15
      if (nowSpeaking !== speaking) {
        speaking = nowSpeaking
        this.updatePeer(peerId, { speaking })
        this.cb.onSpeaking(peerId, speaking)
      }
      requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
  }
}
